import { useState } from 'react'
import api from '../api/axios'
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline'

export interface EmployeeIdTarget {
  userId?: number
  email?: string | null
  name?: string | null
  employeeId: string | null
}

interface Problem {
  code: string
  message: string
}

interface MovedRow {
  table: string
  column: string
  label: string
  count: number
}

interface PreviewResponse {
  current: string
  newEmployeeId: string
  valid: boolean
  problems: Problem[]
  moved: MovedRow[]
  total: number
  columnsChecked: number
  tablesChecked: number
  linked: Array<{ user_id: number; role: string; name: string }>
  person: { user_id: number; name: string; email: string | null; role: string; employee_id: string | null }
}

interface ApplyResponse {
  ok: boolean
  previous: string
  employee_id: string
  users_rows: number
  moved: MovedRow[]
  total: number
  backup: { id?: string; storage?: string; skipped?: boolean; warning?: string | null } | null
  warnings: string[]
}

interface Props {
  companyId: string | number
  target: EmployeeIdTarget
  onClose: () => void
  onChanged?: (newId: string) => void
}

const fmtMoved = (rows: MovedRow[]) => rows.map((r) => `${r.label} ${r.count.toLocaleString()}`).join(', ')

export default function EmployeeIdChangePanel({ companyId, target, onClose, onChanged }: Props) {
  const [value, setValue] = useState('')
  const [checking, setChecking] = useState(false)
  const [applying, setApplying] = useState(false)
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [result, setResult] = useState<ApplyResponse | null>(null)
  const [error, setError] = useState('')
  const [backupFailed, setBackupFailed] = useState(false)

  const who = target.userId != null ? { userId: target.userId } : { email: target.email }
  const label = target.name || target.email || 'this person'

  const check = async () => {
    setChecking(true)
    setError('')
    setPreview(null)
    setResult(null)
    setBackupFailed(false)
    try {
      const { data } = await api.post<PreviewResponse>(
        `/super-admin/companies/${companyId}/users/employee-id/preview`,
        { ...who, newEmployeeId: value.trim() },
      )
      setPreview(data)
    } catch (e: any) {
      setError(e.response?.data?.message || 'Check failed')
    } finally {
      setChecking(false)
    }
  }

  const apply = async (skipBackup = false) => {
    if (!preview?.valid) return
    const confirmText =
      `Change ${label}'s employee ID from ${preview.current} to ${preview.newEmployeeId}?\n\n` +
      (preview.total > 0
        ? `Moves ${preview.total.toLocaleString()} record${preview.total === 1 ? '' : 's'} to the new ID: ${fmtMoved(preview.moved)}.\n`
        : 'Nothing is recorded under the old ID yet.\n') +
      (preview.linked.length ? `Their linked ${preview.linked.map((l) => l.role).join(', ')} account moves too.\n` : '') +
      (skipBackup
        ? '\nNO BACKUP will be taken.\n'
        : '\nA backup of the company database is taken first.\n') +
      '\nMake sure their phone has finished syncing first. They sign in to the app with the new ID afterwards; their PIN stays the same.'
    if (!confirm(confirmText)) return

    setApplying(true)
    setError('')
    setBackupFailed(false)
    try {
      const { data } = await api.post<ApplyResponse>(
        `/super-admin/companies/${companyId}/users/employee-id/apply`,
        { ...who, newEmployeeId: preview.newEmployeeId, expectedCurrent: preview.current, skipBackup },
      )
      setResult(data)
      setPreview(null)
      onChanged?.(data.employee_id)
    } catch (e: any) {
      const code = e.response?.data?.code
      setError(e.response?.data?.message || 'Change failed')
      if (code === 'BACKUP_FAILED') setBackupFailed(true)
      if (code === 'EMPLOYEE_ID_CHANGED' || code === 'TAKEN') setPreview(null)
    } finally {
      setApplying(false)
    }
  }

  return (
    <div className="mt-2 border border-indigo-200 bg-indigo-50/40 rounded-lg p-3 space-y-2 text-sm">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-slate-800">
          Change employee ID <span className="font-normal text-slate-500">· {label} · now</span>{' '}
          <span className="font-mono">{target.employeeId || '(none)'}</span>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-white" aria-label="Close">
          <XMarkIcon className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      {!result && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            inputMode="numeric"
            placeholder="New ID (4 to 10 digits)"
            value={value}
            onChange={(e) => { setValue(e.target.value); setPreview(null) }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void check() } }}
            className="w-48 px-3 py-1.5 text-sm font-mono border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            onClick={() => void check()}
            disabled={checking || !value.trim()}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-white border border-indigo-300 rounded-md hover:bg-indigo-50 disabled:opacity-50"
          >
            {checking && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
            Check
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
          <div>
            {error}
            {backupFailed && preview?.valid && (
              <button onClick={() => void apply(true)} className="ml-2 underline font-medium">
                Apply without a backup
              </button>
            )}
          </div>
        </div>
      )}

      {preview && (
        <div className="space-y-2">
          {preview.problems.length > 0 ? (
            <ul className="text-xs text-red-700 list-disc list-inside space-y-0.5">
              {preview.problems.map((p) => <li key={p.code}>{p.message}</li>)}
            </ul>
          ) : (
            <div className="flex items-center gap-1 text-xs text-emerald-700">
              <CheckCircleIcon className="w-4 h-4" />
              <span className="font-mono">{preview.newEmployeeId}</span> is free.
            </div>
          )}
          <div className="text-xs text-slate-700">
            {preview.total > 0 ? (
              <>
                <span className="font-medium">Moves to the new ID:</span> {fmtMoved(preview.moved)}
              </>
            ) : (
              'Nothing recorded under this ID yet.'
            )}
            {preview.linked.length > 0 && (
              <div className="mt-1">Linked account moves too: {preview.linked.map((l) => `${l.name} (${l.role})`).join(', ')}.</div>
            )}
            <div className="mt-1 text-slate-500">
              Checked {preview.columnsChecked} columns in {preview.tablesChecked} tables. A backup is taken first.
            </div>
          </div>
          {preview.valid && (
            <button
              onClick={() => void apply(false)}
              disabled={applying}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50"
            >
              {applying && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
              {applying ? 'Applying…' : `Apply: ${preview.current} to ${preview.newEmployeeId}`}
            </button>
          )}
        </div>
      )}

      {result && (
        <div className="border border-emerald-200 bg-emerald-50 rounded p-2 text-xs text-emerald-900 space-y-1">
          <div className="flex items-center gap-1 font-semibold text-emerald-800">
            <CheckCircleIcon className="w-4 h-4" /> Employee ID changed: <span className="font-mono">{result.previous}</span> to{' '}
            <span className="font-mono">{result.employee_id}</span>
          </div>
          <div>
            {result.total > 0 ? `Moved ${result.total.toLocaleString()} records: ${fmtMoved(result.moved)}.` : 'No records needed moving.'}
            {result.users_rows > 1 && ` ${result.users_rows} accounts updated (linked account included).`}
          </div>
          <div>
            {result.backup?.skipped ? 'No backup was taken.' : `Backup taken${result.backup?.storage ? ` (${result.backup.storage})` : ''}.`}
          </div>
          <div className="text-slate-700">
            They sign in to the app with the new ID <span className="font-mono">{result.employee_id}</span>; their PIN is unchanged.
            Their phone must sign in again.
          </div>
          {result.warnings.length > 0 && (
            <ul className="text-amber-700 list-disc list-inside">
              {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
